import { useState, useEffect } from 'react';
import { modelsAPI } from '../../api/models';
import { Edit2, Trash2, Plus, X, Save, Eye } from 'lucide-react';
import Loader from '../common/Loader';

export default function AdminModels() {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'website',
    previewImage: '',
    demoUrl: '',
    price: { base: 0, custom: 0 },
    features: [''],
    tags: ['']
  });
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    loadModels();
  }, []);

  const loadModels = async () => {
    try {
      setLoading(true);
      const response = await modelsAPI.getAdminAll();
      setModels(response.data || []);
    } catch (error) {
      console.error('Erreur:', error);
      setModels([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await modelsAPI.update(editingId, formData);
      } else {
        await modelsAPI.create(formData);
      }
      setShowForm(false);
      setEditingId(null);
      resetForm();
      loadModels();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'enregistrement');
    }
  };

  const handleEdit = (model) => {
    setEditingId(model._id);
    setFormData({
      title: model.title,
      description: model.description,
      category: model.category,
      previewImage: model.previewImage || '',
      demoUrl: model.demoUrl || '',
      price: model.price || { base: 0, custom: 0 },
      features: model.features && model.features.length > 0 ? [...model.features] : [''],
      tags: model.tags && model.tags.length > 0 ? [...model.tags] : ['']
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer définitivement ce modèle ?')) {
      return;
    }
    
    try {
      setModels(prevModels => prevModels.filter(model => model._id !== id));
      await modelsAPI.delete(id);
      console.log('Modèle supprimé avec succès');
    } catch (error) {
      console.error('Erreur de suppression:', error);
      alert('Erreur lors de la suppression');
      loadModels();
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'website',
      previewImage: '',
      demoUrl: '',
      price: { base: 0, custom: 0 },
      features: [''],
      tags: ['']
    });
  };

  const addFeature = () => {
    setFormData({ ...formData, features: [...formData.features, ''] });
  };

  const updateFeature = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData({ ...formData, features: newFeatures });
  };

  const addTag = () => {
    setFormData({ ...formData, tags: [...formData.tags, ''] });
  };

  const updateTag = (index, value) => {
    const newTags = [...formData.tags];
    newTags[index] = value;
    setFormData({ ...formData, tags: newTags });
  };

  if (loading) return <Loader />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-6">
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 p-6 shadow-2xl">
        <div className="flex justify-between items-center mb-8 pb-6 border-b border-gray-700/50">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
            Gestion des Modèles
          </h2>
          <button 
            onClick={() => setShowForm(true)} 
            className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-medium rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/20 active:scale-95"
          >
            <Plus size={20} />
            Nouveau Modèle
          </button>
        </div>

        {showForm && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-gray-800 border border-gray-700/50 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center p-6 border-b border-gray-700/50 sticky top-0 bg-gray-800 z-10">
                <h3 className="text-xl font-bold text-white">
                  {editingId ? 'Modifier' : 'Nouveau'} Modèle
                </h3>
                <button 
                  onClick={() => {
                    setShowForm(false);
                    setEditingId(null);
                    resetForm();
                  }} 
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors duration-200"
                >
                  <X size={20} />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-300">
                      Titre *
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-700/50 border border-gray-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-300">
                      Catégorie *
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-700/50 border border-gray-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                      required
                    >
                      <option value="website">Site Web</option>
                      <option value="ecommerce">E-commerce</option>
                      <option value="crm">CRM</option>
                      <option value="portfolio">Portfolio</option>
                      <option value="business">Business</option>
                      <option value="blog">Blog</option>
                    </select>
                  </div>
                  
                  <div className="space-y-2 col-span-1 md:col-span-2">
                    <label className="block text-sm font-medium text-gray-300">
                      Description *
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-700/50 border border-gray-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                      required
                      rows={3}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-300">
                      Image de prévisualisation (URL)
                    </label>
                    <input
                      type="text"
                      value={formData.previewImage}
                      onChange={(e) => setFormData({ ...formData, previewImage: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-700/50 border border-gray-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-300">
                      URL de démo
                    </label>
                    <input
                      type="text"
                      value={formData.demoUrl}
                      onChange={(e) => setFormData({ ...formData, demoUrl: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-700/50 border border-gray-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                      placeholder="https://demo.example.com"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-300">
                      Prix de base (€)
                    </label>
                    <input
                      type="number"
                      value={formData.price.base}
                      onChange={(e) => setFormData({
                        ...formData,
                        price: { ...formData.price, base: parseFloat(e.target.value) || 0 }
                      })}
                      className="w-full px-4 py-2.5 bg-gray-700/50 border border-gray-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                      required
                      min="0"
                      step="0.01"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-300">
                      Prix personnalisé (€)
                    </label>
                    <input
                      type="number"
                      value={formData.price.custom}
                      onChange={(e) => setFormData({
                        ...formData,
                        price: { ...formData.price, custom: parseFloat(e.target.value) || 0 }
                      })}
                      className="w-full px-4 py-2.5 bg-gray-700/50 border border-gray-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Fonctionnalités
                  </label>
                  {formData.features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2 mb-2">
                      <input
                        type="text"
                        value={feature}
                        onChange={(e) => updateFeature(index, e.target.value)}
                        className="flex-1 px-4 py-2.5 bg-gray-700/50 border border-gray-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                        placeholder="Fonctionnalité"
                      />
                      {formData.features.length > 1 && (
                        <button 
                          type="button" 
                          onClick={() => {
                            const newFeatures = formData.features.filter((_, i) => i !== index);
                            setFormData({ ...formData, features: newFeatures });
                          }}
                          className="p-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-md transition-colors duration-200"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                  <button 
                    type="button" 
                    onClick={addFeature}
                    className="px-4 py-2 border border-gray-600 hover:border-blue-400 text-gray-300 hover:text-blue-400 font-medium rounded-lg transition-all duration-300"
                  >
                    + Ajouter une fonctionnalité
                  </button>
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Tags
                  </label>
                  {formData.tags.map((tag, index) => (
                    <div key={index} className="flex items-center gap-2 mb-2">
                      <input
                        type="text"
                        value={tag}
                        onChange={(e) => updateTag(index, e.target.value)}
                        className="flex-1 px-4 py-2.5 bg-gray-700/50 border border-gray-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                        placeholder="Tag"
                      />
                      {formData.tags.length > 1 && (
                        <button 
                          type="button" 
                          onClick={() => {
                            const newTags = formData.tags.filter((_, i) => i !== index);
                            setFormData({ ...formData, tags: newTags });
                          }}
                          className="p-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-md transition-colors duration-200"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                  <button 
                    type="button" 
                    onClick={addTag}
                    className="px-4 py-2 border border-gray-600 hover:border-blue-400 text-gray-300 hover:text-blue-400 font-medium rounded-lg transition-all duration-300"
                  >
                    + Ajouter un tag
                  </button>
                </div>
                
                <div className="flex gap-3 pt-4 border-t border-gray-700/50">
                  <button 
                    type="button" 
                    onClick={() => {
                      setShowForm(false);
                      setEditingId(null);
                      resetForm();
                    }} 
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 font-medium rounded-lg transition-all duration-300 active:scale-95"
                  >
                    Annuler
                  </button>
                  <button 
                    type="submit" 
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-medium rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/20 active:scale-95"
                  >
                    <Save size={20} />
                    {editingId ? 'Mettre à jour' : 'Créer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {models.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <p>Aucun modèle disponible. Cliquez sur "Nouveau Modèle" pour en créer un.</p>
          </div>
        ) : (
          <>
            <div className="mb-6 text-gray-400 text-sm">
              <p>Total: {models.length} modèle{models.length > 1 ? 's' : ''}</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {models.map((model) => (
                <div 
                  key={model._id} 
                  className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl overflow-hidden hover:border-blue-500/30 transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/10 animate-fadeIn"
                >
                  <div className="relative h-48 bg-gradient-to-br from-gray-900 to-gray-800 overflow-hidden">
                    {model.previewImage ? (
                      <img 
                        src={model.previewImage} 
                        alt={model.title} 
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        Aucune image
                      </div>
                    )}
                  </div>
                  
                  <div className="p-5">
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-lg font-bold text-white truncate">
                        {model.title}
                      </h3>
                      <div className="flex items-center gap-1">
                        {model.demoUrl && (
                          <a 
                            href={model.demoUrl} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="p-2 bg-gray-700/50 hover:bg-gray-600 text-gray-300 rounded-lg transition-all duration-200 hover:scale-105"
                            title="Voir la démo"
                          >
                            <Eye size={18} />
                          </a>
                        )}
                        <button 
                          onClick={() => handleEdit(model)} 
                          className="p-2 bg-gray-700/50 hover:bg-gray-600 text-gray-300 rounded-lg transition-all duration-200 hover:scale-105" 
                          title="Modifier"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button 
                          onClick={() => handleDelete(model._id)} 
                          className="p-2 bg-gray-700/50 hover:bg-red-500/20 text-gray-300 hover:text-red-400 rounded-lg transition-all duration-200 hover:scale-105" 
                          title="Supprimer définitivement"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                    
                    <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                      {model.description}
                    </p>
                    
                    <div className="flex items-center justify-between mb-4">
                      <span className="px-3 py-1 bg-blue-500/10 text-blue-400 text-xs font-medium rounded-full">
                        {model.category}
                      </span>
                      <span className="text-lg font-bold text-green-400">
                        {model.price?.base || 0}€
                      </span>
                    </div>
                    
                    {model.tags && model.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {model.tags.map((tag, index) => (
                          <span key={index} className="px-2 py-1 bg-gray-700/50 text-gray-300 text-xs rounded-md">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    {model.features && model.features.length > 0 && (
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-gray-300">
                          <strong>Fonctionnalités :</strong>
                        </p>
                        <ul className="space-y-1">
                          {model.features.slice(0, 3).map((feature, index) => (
                            <li key={index} className="text-gray-400 text-sm">
                              • {feature}
                            </li>
                          ))}
                          {model.features.length > 3 && (
                            <li className="text-gray-400 text-sm">
                              ... et {model.features.length - 3} autres
                            </li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}